import React, { useState, useRef, useEffect, TouchEvent } from 'react';
import { Stepper } from '../Stepper/Stepper';

interface CarouselProps {
	children: React.ReactElement | React.ReactElement[] | undefined;
	dataLength?: number;
	render?: boolean;
	className?: string;
	widthOfElement?: number;
}

export const Carousel = ({
	children,
	dataLength = 0,
	render = true,
	className,
	widthOfElement,
}: CarouselProps) => {
	const [currentStep, setCurrentStep] = useState(0);
	const stepperRef = useRef<HTMLDivElement | null>(null);
	const direction = useRef<null | 'right' | 'left'>(null);
	const startX = useRef(0);
	const startY = useRef(0);
	const elementWidth = widthOfElement ?? 255;

	const handleStepClick = (index: number) => {
		setCurrentStep(index);
	};

	const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
		startX.current = event.touches[0].clientX;
		startY.current = event.touches[0].clientY;
	};

	const handleTouchMove = (event: TouchEvent<HTMLDivElement>) => {
		const touchY = event.touches[0].clientY;
		const deltaY = touchY - startY.current;

		const touchX = event.touches[0].clientX;
		const deltaX = touchX - startX.current;

		if (Math.abs(deltaX) > Math.abs(deltaY)) {
			event.preventDefault();
			event.stopPropagation();
			direction.current = null;

			if (deltaX < 0) {
				// Scrolling left
				direction.current = 'left';
			} else if (deltaX > 0) {
				// Scrolling right
				direction.current = 'right';
			}
		}
	};

	const changeStep = () => {
		if (!render) {
			return;
		}

		if (direction.current === 'left') {
			if (currentStep < dataLength - 1) {
				setCurrentStep(currentStep + 1);
			}
		} else if (direction.current === 'right') {
			if (currentStep > 0) {
				setCurrentStep(currentStep - 1);
			}
		}
	};

	useEffect(() => {
		if (stepperRef.current) {
			stepperRef.current.scrollTo({
				left: currentStep * (elementWidth + 24), // 225px card width + 24 px gap
				behavior: 'smooth',
			});
		}
	}, [currentStep]);

	useEffect(() => {
		if (stepperRef.current) {
			stepperRef.current.addEventListener(
				'touchmove',
				(e) => handleTouchMove(e as unknown as TouchEvent<HTMLDivElement>),
				{ passive: false },
			);
		}

		return () => {
			if (stepperRef.current) {
				stepperRef.current.removeEventListener('touchmove', (e) =>
					handleTouchMove(e as unknown as TouchEvent<HTMLDivElement>),
				);
			}
		};
	}, [stepperRef]);

	return (
		<>
			<div
				ref={stepperRef}
				className="container overflow-x-auto md:overflow-x-hidden no-scrollbar"
				onTouchStart={handleTouchStart}
				onTouchEnd={changeStep}
			>
				<div
					className={`lg:grid lg:grid-cols-4 flex lg:ml-0 ${className}`}
				>
					{React.Children.toArray(children).map((childrenNode, index) => {
							return (
								<div key={index} style={{ minWidth: `${elementWidth + 24}px` }}>
									{childrenNode}
								</div>
							);
					})}
				</div>
			</div>
			<div className="container mx-auto mt-10">
				<Stepper
					totalSteps={dataLength}
					currentStep={currentStep}
					onStepClick={handleStepClick}
					stepperRef={stepperRef}
					className="lg:hidden"
					elementWidth={elementWidth}
				/>
			</div>
		</>
	);
};
